import {
  GridDataSource,
  GridFilterItem,
  GridFilterModel,
  GridGetRowsParams,
  GridLogicOperator,
  GridRowModel,
} from '@mui/x-data-grid-premium'
import { AvailableLocale } from '@omnea-digital/toolbox'
import { CancelledError, hashKey, QueryKey, useQueryClient } from '@tanstack/react-query'
import { RefObject, useMemo, useRef, useState } from 'react'
import { useTranslationsContext } from '@omnea-app/fe-i18n/TranslationsContext'
import { ROW_GROUPING_NONE_GROUPING_TEXT_VALUE } from '@omnea-app/fe-tables/row-grouping/helpers'
import { createQueryKey } from '@omnea-app/fe-tables/utils/createQueryKey'
import {
  resolveGroupingFilterFields,
  transformMuiFilterModelToOmnea,
  transformMuiSortModelToOmnea,
} from '@omnea-app/fe-tables/utils/transformMuiGridModels'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { isUuid } from '@omnea-app/packages/universal/string-utils/isUuid'
import {
  isRequestGroupableField,
  requestTopLevelFilterKeys,
  RequestTopLevelFilterKey,
} from '@omnea-app/requests/requestsFrontendClient'
import { RequestsApiRequestBody, RequestsHttpClient } from '@omnea-app/requests/requestsHttpClient'
import { paths } from '@omnea-app/requests/requestsHttpClient/requestsGeneratedTypes'
import { normaliseRequestFilterModel } from './normaliseRequestFilterModel'
import { requestDataToRequestRows, RequestRow } from './useRequestTableRows'

export const REQUESTS_DATA_SOURCE_QUERY_KEY = 'requests-data-source'

const REQUESTS_V2_ENDPOINT = '/paginated-v2' as const

type RawV2Response = NonNullable<paths['/paginated-v2']['post']['responses'][200]['content']['application/json']>
type RequestsV2RequestBody = NonNullable<RequestsApiRequestBody<'/paginated-v2', 'post'>>
type RequestGroupBy = NonNullable<RequestsV2RequestBody['groupBy']>

type BuildRequestBodyParams = {
  paginationModel: GridGetRowsParams['paginationModel']
  filterModel?: GridGetRowsParams['filterModel']
  sortModel?: GridGetRowsParams['sortModel']
  groupFields?: GridGetRowsParams['groupFields']
  topLevelFilter: RequestTopLevelFilterKey
  locale?: AvailableLocale
  groupBy?: RequestGroupBy
  groupKey?: string | null
}

const isRequestTopLevelFilterKey = (key: string): key is RequestTopLevelFilterKey =>
  requestTopLevelFilterKeys.some((tab) => tab === key)

// A saved view is identified by a UUID `topLevelFilterKey`; the endpoint only accepts concrete
// tab enums, so a saved view (or any unrecognised key) maps to `all` while its clauses ride along
// in `filterModel`.
export const resolveTopLevelFilter = (key: string | undefined): RequestTopLevelFilterKey => {
  if (!key || isUuid(key) || !isRequestTopLevelFilterKey(key)) return 'all'
  return key
}

export const buildRequestsServerFilterSort = ({
  filterModel,
  sortModel,
  groupFields,
  locale,
}: {
  filterModel?: GridGetRowsParams['filterModel']
  sortModel?: GridGetRowsParams['sortModel']
  groupFields?: GridGetRowsParams['groupFields']
  locale?: AvailableLocale
}) => ({
  filterModel: filterModel ? transformMuiFilterModelToOmnea({ filterModel, locale, groupFields }) : undefined,
  sortModel: transformMuiSortModelToOmnea({ sortModel, groupFields }),
})

// A non-removable base scope (e.g. a supplier id) ANDed into every fetch. The server filter model is flat
// with one logic operator, so a user's OR would otherwise OR the scope clause away and leak other entities'
// rows — force AND whenever a base clause is present.
export const mergeBaseFilterItems = (
  filterModel: GridFilterModel,
  baseFilterItems: GridFilterItem[] | undefined
): GridFilterModel => {
  if (!baseFilterItems || baseFilterItems.length === 0) return filterModel
  return {
    ...filterModel,
    items: [...filterModel.items, ...baseFilterItems],
    logicOperator: GridLogicOperator.And,
  }
}

export const buildRequestsV2RequestBody = ({
  paginationModel,
  filterModel,
  sortModel,
  groupFields,
  topLevelFilter,
  locale,
  groupBy,
  groupKey,
}: BuildRequestBodyParams): RequestsV2RequestBody => {
  const { filterModel: omneaFilterModel, sortModel: omneaSortModel } = buildRequestsServerFilterSort({
    filterModel,
    sortModel,
    groupFields,
    locale,
  })
  return {
    paginationModel,
    sortModel: omneaSortModel,
    filterModel: omneaFilterModel,
    topLevelFilter,
    // groupKey undefined (omitted) = root group list; present (value or null) = expand that group.
    ...(groupBy && { groupBy, groupKey }),
  }
}

export const transformV2Response = (
  data: RawV2Response,
  userId: string
): { rows: RequestRow[]; rowCount: number; isApprox: boolean } => ({
  rows: requestDataToRequestRows(data.items, userId),
  rowCount: data.totalCount,
  isApprox: data.totalCountIsApprox ?? false,
})

// A root-level group summary rendered as an expandable row. `groupKey` is the grouped field value
// (null is the "(none)" group); getGroupKey/getChildrenCount below read this shape.
type RequestGroupRow = { id: string; groupKey: string | null; childCount: number }

export const isRequestGroupRow = (row: GridRowModel): row is RequestGroupRow => 'childCount' in row

// The footer/pager binds to the top level (the group list), so an expanded group's children can't be
// paged — fetch them in one capped batch instead. The group header still shows the true size via
// getChildrenCount; a group larger than this shows its first slice.
export const EXPANDED_GROUP_CHILDREN_LIMIT = 100

// The null "(none)" group needs a label that round-trips back to null; `??` (not `||`) maps only null to it.
const NONE_GROUP_LABEL = ROW_GROUPING_NONE_GROUPING_TEXT_VALUE
const encodeGroupKey = (key: string | null): string => key ?? NONE_GROUP_LABEL
const decodeGroupKey = (key: string): string | null => (key === NONE_GROUP_LABEL ? null : key)

const groupsToRows = (groups: NonNullable<RawV2Response['groups']>): RequestGroupRow[] =>
  groups.map((group) => ({ id: `group:${encodeGroupKey(group.key)}`, groupKey: group.key, childCount: group.childCount }))

export type UseRequestsDataSourceOptions = {
  userId: string
  topLevelFilterKey?: string | undefined
  topLevelFilterKeyRef?: RefObject<string | undefined>
  // ANDed into every getRows fetch and non-removable from the grid — a scope the user can't clear (e.g. a
  // supplier id on the supplier requests tab). Absent for the main table, where it is inert.
  baseFilterItems?: GridFilterItem[]
}

export const useRequestsDataSource = (
  getToken: () => Promise<string>,
  options: UseRequestsDataSourceOptions
): { dataSource: GridDataSource; totalCountIsApprox: boolean; loading: boolean; rowCount: number; totalCount: number } => {
  const queryClient = useQueryClient()
  const { locale } = useTranslationsContext()
  const { userId, topLevelFilterKey, topLevelFilterKeyRef, baseFilterItems } = options
  const [totalCountIsApprox, setTotalCountIsApprox] = useState(false)
  const [loading, setLoading] = useState(false)
  const [rowCount, setRowCount] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const inFlightRequests = useRef(0)
  const lastTopLevelKeyRef = useRef<QueryKey | null>(null)

  const dataSource = useMemo<GridDataSource>(
    () => ({
      getRows: async (params: GridGetRowsParams) => {
        const key = topLevelFilterKeyRef?.current ?? topLevelFilterKey
        const topLevelFilter = resolveTopLevelFilter(key)

        // Resolve MUI's group-column pseudo-field to the real field before coercing: coercion is keyed on
        // the field (status), so a group-column status filter left on the pseudo-field would skip coercion
        // and reach v2 as an unserved `status contains`/labelled `equals`, which it 400s.
        const resolvedFilterModel = resolveGroupingFilterFields(params.filterModel, params.groupFields)
        const filterModel = mergeBaseFilterItems(normaliseRequestFilterModel(resolvedFilterModel), baseFilterItems)

        const groupField = params.groupFields?.[0]
        const groupBy = groupField !== undefined && isRequestGroupableField(groupField) ? groupField : undefined
        const groupKeys = params.groupKeys ?? []
        // Single-level grouping: only the first (and only) group key matters; undefined = root list.
        const encodedGroupKey = groupKeys[0]
        const groupKey = groupBy && encodedGroupKey !== undefined ? decodeGroupKey(encodedGroupKey) : undefined
        const isRootGroupFetch = groupBy !== undefined && groupKeys.length === 0
        const isExpandFetch = groupBy !== undefined && groupKeys.length > 0
        // MUI passes the top-level pager's page for a group's child fetch; pin page 0 and take one
        // capped batch so children aren't offset by the group-list page (and can't be paged apart).
        const paginationModel = isExpandFetch ? { page: 0, pageSize: EXPANDED_GROUP_CHILDREN_LIMIT } : params.paginationModel

        // userId is in the key because the row transform derives a per-user flag (isUserAssigned) — a
        // shared key would serve another user's flag.
        // The group model is keyed too so root/expanded/ungrouped fetches don't collide in cache.
        // Key on the effective paginationModel, not params: an expand fetch overrides it to page 0, so
        // keying on the raw top-level page would fragment the same child batch across group-list pages.
        // baseFilterItems is in the key because it's merged into the body, not params.filterModel — two
        // scopes (e.g. different suppliers) sharing a page/sort/user would otherwise collide in cache.
        const queryKey = createQueryKey(
          REQUESTS_DATA_SOURCE_QUERY_KEY,
          { ...params, paginationModel },
          {
            topLevelFilter,
            locale,
            userId,
            ...(baseFilterItems && baseFilterItems.length > 0 && { baseFilterItems: JSON.stringify(baseFilterItems) }),
            ...(groupBy && { groupBy, groupKeys: JSON.stringify(groupKeys) }),
          }
        )

        // Cancel the previous in-flight top-level fetch so a stale one can't land late over the newer
        // rows. Expand (child) fetches are exempt — concurrent drill-ins must all survive.
        if (!isExpandFetch) {
          const previousKey = lastTopLevelKeyRef.current
          if (previousKey && hashKey(previousKey) !== hashKey(queryKey)) {
            void queryClient.cancelQueries({ queryKey: previousKey, exact: true })
          }
          lastTopLevelKeyRef.current = queryKey
        }

        // ref-count concurrent fetches so the load vital only settles when the last one finishes
        inFlightRequests.current += 1
        setLoading(true)
        try {
          const fetched = await queryClient.fetchQuery({
            queryKey,
            queryFn: async ({ signal }) => {
              const token = await getToken()
              const client = new RequestsHttpClient({ authorization: token })

              const { data, error } = await client.POST(REQUESTS_V2_ENDPOINT, {
                body: buildRequestsV2RequestBody({
                  paginationModel,
                  filterModel,
                  sortModel: params.sortModel,
                  groupFields: params.groupFields,
                  topLevelFilter,
                  locale,
                  groupBy,
                  groupKey,
                }),
                signal,
              })

              throwIfApiError(error)
              if (!data) throw new Error(`No data returned from ${REQUESTS_V2_ENDPOINT}`)

              // Root group list: the grid renders these as expandable rows; drilling in re-fetches
              // this endpoint with the group's key as groupKey, hitting the normal item transform.
              if (isRootGroupFetch) {
                return { rows: groupsToRows(data.groups ?? []), rowCount: data.totalCount, isApprox: false }
              }

              return transformV2Response(data, userId)
            },
            staleTime: 300_000, // 5 mins
            gcTime: 600_000,
          })

          if (isExpandFetch) {
            // Omit rowCount: MUI's child-fetch setRowCount is truthy-guarded, so the grid keeps the
            // top-level group total from the root fetch instead of overwriting it with the child count.
            return { rows: fetched.rows }
          }

          // MUI's data source has no out-of-order guard — it applies whatever getRows settles with, so a
          // superseded fetch must never settle back. Cancelling a query that had cached data resolves with
          // stale data (TanStack revert), not CancelledError — hence this key guard alongside the catch below.
          if (lastTopLevelKeyRef.current && hashKey(lastTopLevelKeyRef.current) !== hashKey(queryKey)) {
            return new Promise<never>(() => {})
          }

          // rowCount feeds the table-load RUM vital, so set it only on the main-list fetch — a group
          // drill-in (expand, above) must not re-report the capped child count as a table load.
          setRowCount(fetched.rows.length)
          // The grand total shown in the footer. MUI's rowCount is forced to -1 for an approximate count
          // (below) to keep prev/next enabled, so the real figure is surfaced separately for the label.
          setTotalCount(fetched.rowCount)
          setTotalCountIsApprox(fetched.isApprox)
          return { rows: fetched.rows, rowCount: fetched.isApprox ? -1 : fetched.rowCount }
        } catch (error) {
          // The no-cache cancellation outcome rejects with CancelledError instead; never-settle it too.
          if (error instanceof CancelledError) return new Promise<never>(() => {})
          throw error
        } finally {
          inFlightRequests.current -= 1
          if (inFlightRequests.current === 0) setLoading(false)
        }
      },
      // Leaf rows fall back to their unique id so siblings don't collapse into one tree node.
      getGroupKey: (row) => (isRequestGroupRow(row) ? encodeGroupKey(row.groupKey) : String(row.id)),
      getChildrenCount: (row) => (isRequestGroupRow(row) ? row.childCount : 0),
    }),
    [queryClient, getToken, locale, userId, topLevelFilterKey, topLevelFilterKeyRef, baseFilterItems]
  )

  return { dataSource, totalCountIsApprox, loading, rowCount, totalCount }
}
